import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { getApiUrl } from "../../config/api";
import { BookOpen, GraduationCap, Plus, Trash2, X, Layers, ShieldCheck, ChevronRight, BookText, CheckSquare, Square, CheckCircle2, AlertCircle, Search } from "lucide-react";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";

interface Subject { id: string; name: string; }
interface Class { id: string; name: string; teacher?: { id: string; name: string } | null; }

const ClassSubjects: React.FC = () => {
  const [classes, setClasses] = useState<Class[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [classSubjects, setClassSubjects] = useState<Subject[]>([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBulkAssigning, setIsBulkAssigning] = useState(false);
  const [selectedSubjectIds, setSelectedSubjectIds] = useState<Set<string>>(new Set());
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [availableSubjectSearch, setAvailableSubjectSearch] = useState("");
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const token = localStorage.getItem("token");
  const [searchParams] = useSearchParams();
// Auto-select class from URL query parameter
  useEffect(() => {
    const classIdFromUrl = searchParams.get("classId");
    if (classIdFromUrl && classes.length > 0) {
      const classExists = classes.find(c => c.id === classIdFromUrl);
      if (classExists && selectedClass !== classIdFromUrl) {
        setSelectedClass(classIdFromUrl);
      }
    }
  }, [searchParams, classes, selectedClass]);

  const fetchClasses = useCallback(async () => {
    try {
      const res = await axios.get(getApiUrl("academics/classes"), { headers: { Authorization: `Bearer ${token}` } });
      const cls = Array.isArray(res.data) ? res.data : res.data.classes || [];
      setClasses(cls);
    } catch { }
  }, [token]);

  const fetchSubjects = useCallback(async () => {
    try {
      const res = await axios.get(getApiUrl("academics/subjects"), { headers: { Authorization: `Bearer ${token}` } });
      setSubjects(Array.isArray(res.data) ? res.data : res.data.subjects || []);
    } catch { }
  }, [token]);

  const fetchClassSubjects = useCallback(async () => {
    if (!selectedClass) { setClassSubjects([]); return; }
    setLoading(true);
    try {
      const res = await axios.get(getApiUrl(`academics/classes/${selectedClass}/subjects`), { headers: { Authorization: `Bearer ${token}` } });
      setClassSubjects(Array.isArray(res.data) ? res.data : []);
    } catch { setClassSubjects([]); }
    finally { setLoading(false); }
  }, [selectedClass, token]);

  useEffect(() => { if (token) { fetchClasses(); fetchSubjects(); } }, [fetchClasses, fetchSubjects, token]);
  useEffect(() => { fetchClassSubjects(); }, [fetchClassSubjects]);

  const availableSubjects = subjects.filter(sub => !classSubjects.some(cs => cs.id === sub.id));
  const filteredClassSubjects = classSubjects.filter(s => s.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const totalPages = Math.ceil(filteredClassSubjects.length / itemsPerPage);
  const currentClassSubjects = filteredClassSubjects.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleAssignSubject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClass || !selectedSubject) return;
    setIsSubmitting(true);
    setError("");
    setMessage("");
    try {
      await axios.post(getApiUrl(`academics/classes/${selectedClass}/subjects`), { subjectId: selectedSubject }, { headers: { Authorization: `Bearer ${token}` } });
      setMessage("Subject assigned successfully");
      setSelectedSubject("");
      fetchClassSubjects();
      setTimeout(() => {
        setMessage("");
        setIsAssignmentModalOpen(false);
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to assign subject");
      setTimeout(() => setError(""), 3000);
    }
    finally { setIsSubmitting(false); }
  };

  const handleToggleSubjectSelection = (subjectId: string) => {
    setSelectedSubjectIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(subjectId)) {
        newSet.delete(subjectId);
      } else {
        newSet.add(subjectId);
      }
      return newSet;
    });
  };

  const handleSelectAllSubjects = () => {
    const filtered = availableSubjects.filter(s => 
      s.name.toLowerCase().includes(availableSubjectSearch.toLowerCase())
    );
    if (selectedSubjectIds.size === filtered.length && 
        filtered.every(s => selectedSubjectIds.has(s.id))) {
      setSelectedSubjectIds(new Set());
    } else {
      setSelectedSubjectIds(new Set(filtered.map(s => s.id)));
    }
  };

  const handleBulkAssignSubjects = async () => {
    if (!selectedClass || selectedSubjectIds.size === 0) return;
    setIsBulkAssigning(true);
    setError("");
    setMessage("");
    try {
      const subjectIdsArray = Array.from(selectedSubjectIds);
      const res = await axios.post(
        getApiUrl(`academics/classes/${selectedClass}/subjects/bulk`),
        { subjectIds: subjectIdsArray },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage(res.data.message || `Successfully assigned ${res.data.results?.success?.length || 0} subject(s)`);
      setSelectedSubjectIds(new Set());
      fetchClassSubjects();
      setTimeout(() => {
        setMessage("");
        setIsAssignmentModalOpen(false);
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to assign subjects");
      setTimeout(() => setError(""), 5000);
    } finally {
      setIsBulkAssigning(false);
    }
  };

  const handleRemoveSubject = async (id: string) => {
    if (!window.confirm("Dissociate this curriculum from class registry?")) return;
    try {
      await axios.delete(getApiUrl(`academics/classes/${selectedClass}/subjects/${id}`), { headers: { Authorization: `Bearer ${token}` } });
      fetchClassSubjects();
    } catch { }
  };

  const filteredAvailableSubjects = availableSubjects.filter(s => 
    s.name.toLowerCase().includes(availableSubjectSearch.toLowerCase())
  );

  const selectedClassName = classes.find(c => c.id === selectedClass)?.name || "Select Registry";

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">
        <div className="page-title-section">
          <div>
            <h1>Curriculum Architecture</h1>
            <p className="page-subtitle">Mapping academic domains to specific graduation pathways</p>
          </div>
          <div className="flex gap-3">
            <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl border border-slate-100 shadow-sm transition-all hover:border-teal-200">
              <Layers size={16} className="text-teal-500" />
              <select className="bg-transparent border-none outline-none text-xs font-black uppercase tracking-widest text-slate-500 cursor-pointer" value={selectedClass} onChange={e => {
                setSelectedClass(e.target.value);
                setSelectedSubjectIds(new Set());
              }}>
                <option value="">Choose Pathway Registry...</option>
                {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            {selectedClass && (
              <button
                onClick={() => setIsAssignmentModalOpen(true)}
                className="utopia-btn-primary flex items-center gap-2 px-6 py-3 rounded-2xl shadow-lg shadow-teal-100/50"
              >
                <Plus size={18} /> Assign Subjects
              </button>
            )}
          </div>
        </div>

        <div className="p-8 pt-0">
          {/* Main Display */}
          <div>
            <div className="utopia-page-container">
              <div className="utopia-table-header p-8 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-black">{selectedClassName} <span className="text-slate-300 mx-2">/</span> Subjects</h2>
                  <p className="text-xs text-slate-400 mt-1">Authorized curricula units for selected academic pathway</p>
                </div>
                <div className="bg-teal-50 text-teal-600 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border border-teal-100 flex items-center gap-2">
                  <Layers size={14} /> {filteredClassSubjects.length} Registered Units
                </div>
              </div>

              {loading ? (
                <div className="p-32 text-center text-slate-200"><Loader2 size={40} className="animate-spin mx-auto" /></div>
              ) : !selectedClass ? (
                <div className="p-40 text-center text-slate-300">
                  <GraduationCap size={80} className="mx-auto mb-6 opacity-5" />
                  <p className="font-medium text-slate-400">Navigate to an academic registry to visualize curriculum distribution.</p>
                </div>
              ) : filteredClassSubjects.length === 0 ? (
                <div className="p-40 text-center text-slate-300">
                  <Search size={80} className="mx-auto mb-6 opacity-5" />
                  <p className="font-medium text-slate-400">No curricula units mapped to "{selectedClassName}" pathway.</p>
                </div>
              ) : (
                <>
                  <table className="utopia-table group/table">
                    <thead>
                      <tr>
                        <th className="w-16">Unit</th>
                        <th>Subject Paradigm</th>
                        <th>Unit Identification</th>
                        <th className="text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentClassSubjects.map((sub, idx) => (
                        <tr key={sub.id} className="group/row">
                          <td><span className="text-[10px] font-black text-slate-300 font-mono">#{(idx + 1 + (currentPage - 1) * itemsPerPage).toString().padStart(2, '0')}</span></td>
                          <td>
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-400 group-hover/row:bg-teal-50 group-hover/row:text-teal-600 transition-all shadow-inner">
                                <BookText size={18} />
                              </div>
                              <span className="font-black text-slate-800 tracking-tight text-sm uppercase">{sub.name}</span>
                            </div>
                          </td>
                          <td className="text-slate-400 font-mono text-[9px] uppercase tracking-widest">{sub.id}</td>
                          <td className="text-right">
                            <button className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all" onClick={() => handleRemoveSubject(sub.id)}>
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {totalPages > 1 && (
                    <div className="utopia-pagination">
                      <button className="utopia-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}><ChevronRight size={16} className="rotate-180" /></button>
                      <div className="flex gap-2 mx-4">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                          <button key={p} className={`utopia-page-btn ${currentPage === p ? 'active' : ''}`} onClick={() => setCurrentPage(p)}>{p}</button>
                        ))}
                      </div>
                      <button className="utopia-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}><ChevronRight size={16} /></button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Subject Assignment Modal */}
      {isAssignmentModalOpen && (
        <div className="utopia-modal-overlay" onClick={(e) => e.target === e.currentTarget && setIsAssignmentModalOpen(false)}>
          <div className="utopia-modal-content max-w-4xl w-full h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="utopia-modal-header">
              <div>
                <h3 className="text-xl font-bold">Assign Subjects to {selectedClassName}</h3>
                <p className="text-sm text-slate-500 mt-1">Add single or multiple subjects to this class</p>
              </div>
              <button className="p-2 hover:bg-slate-50 rounded-full" onClick={() => setIsAssignmentModalOpen(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {message && (
                <div className="mb-4 p-3 bg-teal-50 border border-teal-100 rounded-lg text-teal-700 text-xs flex items-center gap-2">
                  <CheckCircle2 size={14} /> {message}
                </div>
              )}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg text-red-700 text-xs flex items-center gap-2">
                  <AlertCircle size={14} /> {error}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left: Single Assignment */}
                <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                  <h3 className="text-sm font-black uppercase tracking-widest text-slate-800 mb-6 flex items-center gap-2">
                    <ShieldCheck size={16} className="text-teal-500" /> Single Assignment
                  </h3>
                  
                  <form onSubmit={handleAssignSubject} className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-600">Select Subject</label>
                      <select 
                        className="utopia-input w-full h-12 text-sm font-semibold" 
                        value={selectedSubject} 
                        onChange={e => setSelectedSubject(e.target.value)} 
                        required 
                        disabled={!selectedClass}
                      >
                        <option value="">{selectedClass ? "Choose Subject..." : "Select class first"}</option>
                        {availableSubjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                    <button 
                      type="submit" 
                      className="utopia-btn-primary w-full py-3 shadow-lg shadow-teal-100 flex items-center justify-center gap-2" 
                      disabled={isSubmitting || !selectedSubject}
                    >
                      {isSubmitting ? <Loader2 className="animate-spin" size={18} /> : <ShieldCheck size={18} />}
                      Assign Subject
                    </button>
                  </form>
                </div>

                {/* Right: Bulk Assignment */}
                <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                  <h3 className="text-sm font-black uppercase tracking-widest text-slate-800 mb-6 flex items-center gap-2">
                    <Layers size={16} className="text-teal-500" /> Bulk Assignment
                    {selectedSubjectIds.size > 0 && (
                      <span className="ml-2 px-2 py-0.5 bg-teal-500 text-white text-xs rounded-full">
                        {selectedSubjectIds.size} selected
                      </span>
                    )}
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="relative">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search subjects..."
                        className="utopia-input pl-10 h-10 text-sm"
                        value={availableSubjectSearch}
                        onChange={(e) => setAvailableSubjectSearch(e.target.value)}
                      />
                    </div>
                    
                    {filteredAvailableSubjects.length > 0 && (
                      <>
                        <button
                          onClick={handleSelectAllSubjects}
                          className="w-full px-3 py-2 text-xs font-semibold bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 flex items-center justify-center gap-2"
                        >
                          {selectedSubjectIds.size === filteredAvailableSubjects.length && 
                           filteredAvailableSubjects.every(s => selectedSubjectIds.has(s.id)) ? (
                            <>
                              <CheckSquare size={14} /> Deselect All
                            </>
                          ) : (
                            <>
                              <Square size={14} /> Select All ({filteredAvailableSubjects.length})
                            </>
                          )}
                        </button>
                        
                        {selectedSubjectIds.size > 0 && (
                          <button
                            onClick={handleBulkAssignSubjects}
                            disabled={isBulkAssigning}
                            className="w-full py-3 px-4 bg-teal-500 text-white text-sm font-semibold rounded-lg hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-teal-100"
                          >
                            {isBulkAssigning ? (
                              <>Assigning...</>
                            ) : (
                              <>
                                <Plus size={16} /> Assign {selectedSubjectIds.size} Subject{selectedSubjectIds.size > 1 ? 's' : ''}
                              </>
                            )}
                          </button>
                        )}
                        
                        <div className="max-h-64 overflow-y-auto space-y-2 border border-slate-100 rounded-lg p-3 bg-slate-50">
                          {filteredAvailableSubjects.map(s => (
                            <div
                              key={s.id}
                              className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all ${
                                selectedSubjectIds.has(s.id)
                                  ? 'bg-teal-50 border border-teal-200'
                                  : 'bg-white border border-slate-100 hover:border-slate-200'
                              }`}
                              onClick={() => handleToggleSubjectSelection(s.id)}
                            >
                              {selectedSubjectIds.has(s.id) ? (
                                <CheckSquare size={16} className="text-teal-500 flex-shrink-0" />
                              ) : (
                                <Square size={16} className="text-slate-300 flex-shrink-0" />
                              )}
                              <span className="text-sm font-semibold text-slate-700">{s.name}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                    
                    {filteredAvailableSubjects.length === 0 && availableSubjectSearch && (
                      <p className="text-xs text-slate-400 text-center py-4">No subjects found</p>
                    )}
                    {availableSubjects.length === 0 && (
                      <p className="text-xs text-slate-400 text-center py-4">All subjects assigned</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassSubjects;
const Loader2 = ({ size, className }: any) => <svg className={`${className}`} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56" /></svg>;











